import { Link, usePage } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"

import LinkPills from "../domains/links/pages/LinkPills"
import { csrfToken } from "./csrf"
import type { SharedData } from "./shared"

interface LayoutProps {
  children: ReactNode
  // t010 `impl-public` (R-2b): every page but /contacts uses gap-4; the
  // contract draws /contacts' own <main> at gap-5 (docs/design/
  // contacts.html:101). Defaulted and additive, the same
  // `AdminLayout.tsx` precedent (its own `mainClassName` prop) — every
  // other page's <main> stays byte-identical.
  mainClassName?: string
}

const DEFAULT_MAIN_CLASSES = "flex flex-col gap-4 px-4 py-4"

// t012 (FR-14): the pill segment's active/inactive classes, verbatim from
// docs/design/burger.html — a small point-scale helper, not a component,
// since the only variation between the two segments is which of these
// applies.
function pillClass(active: boolean): string {
  return active
    ? "rounded-full bg-accent/5 px-2.5 py-[3px] text-accent"
    : "px-2.5 py-[3px] text-gray-500"
}

// t010 `impl-public-tab` (FR-5.3, R-2a): the active tab's classes are the
// landed accent treatment; the inactive tab keeps `text-gray-400` — the
// contract draws `text-gray-500` for this header, but FR-5.3 overrides it
// here (the admin header, where nothing has landed yet, is where the
// contract's own color governs).
function tabClass(active: boolean): string {
  return active
    ? "border-b-2 border-accent pb-2 font-medium text-accent"
    : "pb-2 text-gray-400"
}

// t011 `impl-chrome` (FR-9.1): the burger's «Управление» target, derived
// from the current URL rather than hard-wired to `/admin` — the same
// "derive from the current URL" idiom `contactsActive` below already
// uses. `/admin/items/:id/edit` is deliberately not one of the derived
// targets: that route is declared `shallow: true` (config/routes.rb), so
// it carries no date segment to derive a day target from, and falls
// through to the `/admin` default with every other admin address (plan
// R-1, owner-confirmed 2026-08-25).
function manageHref(url: string): string {
  const dayMatch = url.match(/^\/days\/(\d{4}-\d{2}-\d{2})/)
  if (dayMatch) return `/admin/days/${dayMatch[1]}`
  if (url.startsWith("/contacts")) return "/admin/contacts"
  // t020 `impl-chrome` (FR-5, AC-19): the third public tab's own
  // derivation, the `/contacts` branch above's own precedent -- `flights`
  // does have a public page, unlike `stays`, so this joins `/contacts`
  // rather than falling through to the bare `/admin` default.
  if (url.startsWith("/flights")) return "/admin/flights"
  return "/admin"
}

// The app shell: trip name and the role-aware burger menu on top, section
// tabs below, one mobile-first column (max-w-xl mx-auto — inertia-react-
// standards § UI). Every Inertia page wraps its content in this
// component. Every string here is Inertia shared data resolved
// server-side from config/locales/{en,ru}.yml — Layout performs no locale
// resolution of its own. Structure and classes follow
// docs/design/main-page.html verbatim (FR-10) for the header/tabs, and —
// since t012 — docs/design/burger.html verbatim for the menu card in both
// its member and admin variants (FR-13), replacing t009's own
// no-static-contract build (that plan's Risk 4: "the burger has no static
// contract in the repository").
export default function Layout({ children, mainClassName = DEFAULT_MAIN_CLASSES }: LayoutProps) {
  const { props: { ui, tripTitle, tripLinks, isAdmin, locale }, url } = usePage<SharedData>()
  // t010 `impl-public-tab` (FR-5.3, FR-5.4): contacts is active for
  // `/contacts` and every address under it; schedule is active
  // everywhere else, so `/days/:date` keeps its own tab lit rather than
  // falling through to neither. t017 (FR-1.4, AC-13): `/profile` is a
  // third state -- neither tab lights for it, so schedule's own
  // "active" no longer simply means "not contacts".
  const contactsActive = url.startsWith("/contacts")
  const profileActive = url.startsWith("/profile")
  // t020 `impl-chrome` (FR-5.1, AC-19): the third public tab's own active
  // state, the same "derive from the current URL" idiom as contactsActive
  // -- folded into scheduleActive below like every other non-schedule
  // state, so `/flights` lights this tab alone and nothing else.
  const flightsActive = url.startsWith("/flights")
  // t028 `impl-chrome` (FR-11.1): the fourth public tab's own active
  // state, the flightsActive idiom immediately above -- folded into
  // scheduleActive below like every other non-schedule state, so
  // `/images` lights this tab alone and nothing else.
  const imagesActive = url.startsWith("/images")
  const scheduleActive = !contactsActive && !profileActive && !flightsActive && !imagesActive
  const [menuOpen, setMenuOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  // FR-18/FR-19/FR-20: an open menu closes on a tap/click outside the card
  // or on Escape, returning focus to the trigger. The listeners are wired
  // only while the menu is open (and torn down on close or unmount), so
  // Escape with the menu closed does nothing and no listener outlives the
  // component. `mousedown`, not `click` or `pointerdown`: it is what jsdom
  // drives reliably and what a tap emits on mobile. The trigger counts as
  // "inside" here so its own onClick toggle is not immediately re-fought by
  // this handler — otherwise a click on the trigger would close the menu
  // via this handler and then reopen it via onClick (or the reverse),
  // double-toggling instead of toggling once.
  useEffect(() => {
    if (!menuOpen) return

    function close() {
      setMenuOpen(false)
      triggerRef.current?.focus()
    }

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (cardRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      close()
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close()
    }

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [menuOpen])

  return (
    <div className="mx-auto min-h-screen max-w-xl">
      <header className="border-b border-gray-200 px-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-semibold">{tripTitle}</h1>
          {/* t009 (FR-5): the plain sign-out button becomes a burger
              trigger opening a small dropdown card. t017 (FR-8.1)
              reorders and extends it: every signed-in user sees
              «Профиль», «Язык», then a divider; an admin sees
              «Управление» and a second divider before that; «Выйти» is
              always last. The trigger keeps the previous button's own
              position; its size, weight and colour now follow
              docs/design/burger.html (t012, superseding t009's own
              16px/gray-500 glyph). */}
          <div className="relative">
            {/* A disclosure button (WAI-ARIA "Disclosure" pattern: a
                toggle button plus aria-expanded, nothing more elaborate)
                rather than a full ARIA menu widget — this opens/closes a
                small card of ordinary links and a form button, with no
                arrow-key roving focus to implement, so role="menu"/
                "menuitem" would claim keyboard behaviour this does not
                have. The glyph is decorative (aria-hidden below), so
                `ui.menu` carries the button's only accessible name — the
                same icon-only-control pattern Day.tsx's ArrowLink already
                uses (t008 FR-12) — rather than leaving an unnamed button
                as the entry point to sign-out and «Управление» (revision:
                the previous plain «Выйти» button this replaces was itself
                its own accessible name). */}
            <button
              ref={triggerRef}
              type="button"
              aria-expanded={menuOpen}
              aria-label={ui.menu}
              onClick={() => setMenuOpen((open) => !open)}
              className="flex items-center py-1 text-gray-700"
            >
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M3 5h14M3 10h14M3 15h14"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {menuOpen && (
              <div
                ref={cardRef}
                className="absolute right-0 top-full z-10 mt-2 flex w-[210px] flex-col rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg"
              >
                {/* t017 (FR-8.1, FR-8.2): the card's own first row,
                    unconditional -- every signed-in user, admins
                    included, no divider before the «Язык» row that
                    follows it (docs/design/burger.html's own two states
                    both draw the two rows back to back). */}
                <Link
                  href="/profile"
                  className="flex h-11 items-center gap-2.5 px-3 text-[15px] text-gray-900"
                >
                  <svg
                    aria-hidden="true"
                    className="shrink-0 text-gray-500"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.4" />
                    <path
                      d="M2.5 14c.6-2.9 2.8-4.5 5.5-4.5s4.9 1.6 5.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  {ui.profile}
                </Link>
                {/* t012 (FR-12 to FR-16): the «Язык» row — a label plus a
                    RU|EN pill switch, each segment a submit button of one
                    PATCH /locale form (same mechanics as «Выйти» below).
                    The highlighted segment reflects the shared `locale`
                    the page actually rendered in, not the stored
                    preference (FR-14), so a never-chosen user still sees a
                    segment highlighted. Both segments stay ordinary,
                    enabled submit buttons — including the active one — so
                    tapping it is a harmless no-op save rather than a
                    disabled control (FR-16/AC-9; a `disabled` attribute
                    would also fall outside the app-wide cursor rule's
                    `:not(:disabled)` scope). */}
                <div className="flex h-11 items-center gap-2.5 px-3 text-[15px] text-gray-900">
                  <svg
                    aria-hidden="true"
                    className="shrink-0 text-gray-500"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
                    <path
                      d="M2 8h12M8 2c2.2 2 2.2 10 0 12M8 2c-2.2 2-2.2 10 0 12"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="flex-1">{ui.language}</span>
                  <form
                    method="post"
                    action="/locale"
                    className="flex rounded-full border border-gray-200 p-0.5 text-xs font-semibold leading-4"
                  >
                    <input type="hidden" name="_method" value="patch" />
                    <input type="hidden" name="authenticity_token" value={csrfToken()} />
                    <button
                      type="submit"
                      name="locale"
                      value="ru"
                      aria-pressed={locale === "ru"}
                      className={pillClass(locale === "ru")}
                    >
                      {ui.localeRu}
                    </button>
                    <button
                      type="submit"
                      name="locale"
                      value="en"
                      aria-pressed={locale === "en"}
                      className={pillClass(locale === "en")}
                    >
                      {ui.localeEn}
                    </button>
                  </form>
                </div>
                <div className="mx-2 my-1 border-t border-gray-100" />
                {/* t017 (FR-8.1): «Управление» moves below «Язык», behind
                    its own second divider -- was the card's first row
                    (t009), now the second group, superseded by
                    «Профиль» above. */}
                {isAdmin && (
                  <>
                    <Link
                      href={manageHref(url)}
                      className="flex h-11 items-center gap-2.5 px-3 text-[15px] text-gray-900"
                    >
                      <svg
                        aria-hidden="true"
                        className="shrink-0 text-gray-500"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M2 4.5h6M12.5 4.5H14M2 11.5h1.5M6.2 11.5H14"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                        />
                        <circle cx="10.3" cy="4.5" r="1.8" stroke="currentColor" strokeWidth="1.4" />
                        <circle cx="4" cy="11.5" r="1.8" stroke="currentColor" strokeWidth="1.4" />
                      </svg>
                      {ui.manage}
                    </Link>
                    <div className="mx-2 my-1 border-t border-gray-100" />
                  </>
                )}
                {/* t006's sign-out control, unchanged in mechanics — same
                    wording, HTTP verb (a real browser POST with a method
                    override) and post-sign-out destination as before, now
                    the menu's own sign-out item. See app/shared/csrf.ts
                    for why this bypasses Inertia's request layer. */}
                <form method="post" action="/sign_out">
                  <input type="hidden" name="_method" value="delete" />
                  <input type="hidden" name="authenticity_token" value={csrfToken()} />
                  <button
                    type="submit"
                    className="flex h-11 w-full items-center gap-2.5 px-3 text-left text-[15px] text-gray-900"
                  >
                    <svg
                      aria-hidden="true"
                      className="shrink-0 text-gray-500"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M6 2H3.5A1.5 1.5 0 002 3.5v9A1.5 1.5 0 003.5 14H6M10.5 11.5L14 8l-3.5-3.5M14 8H6"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {ui.signOut}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
        {/* LinkPills renders nothing for an empty array, so no conditional here. */}
        <LinkPills links={tripLinks} variant="accent" marginClassName="mt-2" />
        <nav className="mt-3 flex gap-6 text-sm">
          <Link
            href="/"
            className={tabClass(scheduleActive)}
            aria-current={scheduleActive ? "page" : undefined}
          >
            {ui.scheduleTab}
          </Link>
          <Link
            href="/contacts"
            className={tabClass(contactsActive)}
            aria-current={contactsActive ? "page" : undefined}
          >
            {ui.contactsTab}
          </Link>
          <Link
            href="/flights"
            className={tabClass(flightsActive)}
            aria-current={flightsActive ? "page" : undefined}
          >
            {ui.flightsTab}
          </Link>
          <Link
            href="/images"
            className={tabClass(imagesActive)}
            aria-current={imagesActive ? "page" : undefined}
          >
            {ui.imagesTab}
          </Link>
        </nav>
      </header>
      <main className={mainClassName}>{children}</main>
    </div>
  )
}
